<template>
  <div class="relative aspect-video">
    <div ref="rootEl" class="relative aspect-video h-auto w-full max-w-full cursor-pointer bg-black" v-bind="rootAttrs">
      <div ref="youtubeEl" class="absolute left-0 top-0 size-full" />
      <img v-if="!ready" :src="placeholder" alt="" loading="lazy" class="size-full object-contain" />
      <div v-if="status === 'awaitingLoad'" class="group absolute inset-0 flex size-full cursor-pointer items-center justify-center">
        <button class="flex size-8 items-center rounded-full bg-background text-text group-hover:bg-background-light-strong group-focus:bg-background-light-strong">
          <Icon name="play" class="mx-auto size-6" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/// <reference types="youtube" />
import { computed, onMounted, ref, watch } from 'vue'
import type { HTMLAttributes, Ref } from 'vue'
import Icon from '~/components/icon/Icon.vue'

const props = defineProps<{
  videoId: string
}>()

const rootEl = useTemplateRef('rootEl')
const youtubeEl = useTemplateRef('youtubeEl')
const ready = ref(false)
const trigger = useScriptTriggerElement({ trigger: 'mousedown', el: rootEl })
const { onLoaded, status } = useScriptYouTubePlayer({
  scriptOptions: {
    trigger,
  },
})

const player: Ref<YT.Player | undefined> = ref()
let clickTriggered = false
if (trigger instanceof Promise) {
  trigger.then((triggered) => {
    if (triggered) {
      clickTriggered = true
    }
  })
}

onMounted(() => {
  onLoaded(async (instance) => {
    const YouTube = instance.YT instanceof Promise ? await instance.YT : instance.YT
    await new Promise<void>((resolve) => {
      if (typeof YT.Player === 'undefined') YouTube.ready(resolve)
      else resolve()
    })
    player.value = new YT.Player(youtubeEl.value!, {
      ...props,
      events: {
        onReady() {
          ready.value = true
          if (clickTriggered) {
            player.value?.playVideo()
            clickTriggered = false
          }
          watch(
            () => props.videoId,
            () => {
              player.value?.loadVideoById(props.videoId)
            }
          )
        },
      },
    })
  })
})

const rootAttrs = computed(() => {
  return {
    'aria-busy': status.value === 'loading',
    'aria-label': status.value === 'awaitingLoad' ? 'YouTube Player - Placeholder' : status.value === 'loading' ? 'YouTube Player - Loading' : 'YouTube Player - Loaded',
    'aria-live': 'polite',
    role: 'application',
    ...(trigger instanceof Promise ? trigger.ssrAttrs || {} : {}),
  } satisfies HTMLAttributes
})

const placeholder = computed(() => `https://i.ytimg.com/vi/${props.videoId}/maxresdefault.jpg`)

if (import.meta.server) {
  useHead({
    link: [
      {
        key: 'nuxt-script-youtube-img',
        rel: 'dns-prefetch',
        href: 'https://i.ytimg.com',
      },
    ],
  })
}
</script>
